import { useState } from 'react';
import { motion } from 'framer-motion';
import { X, Check, Upload, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Smooth easing
const smoothEase = [0.4, 0, 0.2, 1];

const workTypeOptions = [
  { value: '', label: 'Select type of work...' },
  { value: 'cover-art', label: 'Cover Art / Album Artwork' },
  { value: 'flyers-posters', label: 'Flyers & Posters' },
  { value: 'branding', label: 'Branding / Logo Design' },
  { value: 'social-media', label: 'Social Media Assets' },
  { value: 'web-design', label: 'Web / Landing Page Design' },
  { value: 'merch', label: 'Merchandise Design' },
  { value: 'motion', label: 'Motion Graphics / Animation' },
  { value: 'print', label: 'Print Materials' },
  { value: 'mixed', label: 'Multiple / Mixed Project' },
  { value: 'other', label: 'Other (describe below)' }
];

const assetCountOptions = [
  { value: '', label: 'Select number of assets...' },
  { value: '1', label: '1 asset' },
  { value: '2-3', label: '2-3 assets' },
  { value: '4-6', label: '4-6 assets' },
  { value: '7-10', label: '7-10 assets' },
  { value: '10+', label: '10+ assets' },
  { value: 'ongoing', label: 'Ongoing / Recurring' },
  { value: 'unsure', label: 'Not sure yet' }
];

const brandAssetOptions = [
  { id: 'has-logo', label: 'I have a logo' },
  { id: 'has-colors', label: 'I have brand colors' },
  { id: 'has-fonts', label: 'I have brand fonts' },
  { id: 'has-guidelines', label: 'I have brand guidelines' },
  { id: 'need-all', label: 'I need everything created' }
];

const CustomProjectModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    projectDescription: '',
    workType: '',
    assetCount: '',
    brandAssets: [],
    timeline: '',
    notes: '',
    files: []
  });
  
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');
  
  if (!isOpen) return null;
  
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    }
    
    if (!formData.projectDescription.trim()) {
      newErrors.projectDescription = 'Please describe your project';
    }
    
    if (!formData.workType) {
      newErrors.workType = 'Please select the type of work';
    }
    
    if (!formData.assetCount) {
      newErrors.assetCount = 'Please select the number of assets';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };
  
  const handleBrandAssetToggle = (assetId) => {
    setFormData(prev => ({
      ...prev,
      brandAssets: prev.brandAssets.includes(assetId)
        ? prev.brandAssets.filter(id => id !== assetId)
        : [...prev.brandAssets, assetId]
    }));
  };
  
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setFormData(prev => ({ ...prev, files }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const submitData = new FormData();
      submitData.append('fullName', formData.fullName);
      submitData.append('email', formData.email);
      submitData.append('phone', formData.phone);
      submitData.append('projectDescription', formData.projectDescription);
      submitData.append('workType', formData.workType);
      submitData.append('assetCount', formData.assetCount);
      submitData.append('brandAssets', JSON.stringify(formData.brandAssets));
      submitData.append('timeline', formData.timeline);
      submitData.append('notes', formData.notes);
      
      formData.files.forEach((file) => {
        submitData.append('files', file);
      });
      
      const response = await fetch(`${BACKEND_URL}/api/custom-project`, {
        method: 'POST',
        body: submitData
      });
      
      if (!response.ok) {
        throw new Error('Submission failed');
      }
      
      setIsSubmitted(true);
    } catch (error) {
      setSubmitError('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleClose = () => {
    // Reset form state when closing
    setFormData({
      fullName: '',
      email: '',
      phone: '',
      projectDescription: '',
      workType: '',
      assetCount: '',
      brandAssets: [],
      timeline: '',
      notes: '',
      files: []
    });
    setErrors({});
    setIsSubmitted(false);
    setSubmitError('');
    onClose();
  };
  
  // Success state
  if (isSubmitted) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.4, ease: smoothEase }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
      >
        <motion.div 
          className="absolute inset-0 bg-background/95 backdrop-blur-sm"
          onClick={handleClose}
        />
        
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: smoothEase }}
          className="relative z-10 w-full max-w-md bg-card border border-border p-10 text-center"
        >
          <div className="w-16 h-16 bg-accent rounded-full flex items-center justify-center mx-auto mb-8">
            <Check className="w-8 h-8 text-background" />
          </div>
          <h3 className="font-display text-3xl text-foreground mb-4">REQUEST RECEIVED</h3>
          <p className="font-body text-muted-foreground mb-8">
            We'll review your project details and get back to you with a game plan within 24-48 hours.
          </p>
          <button
            onClick={handleClose}
            data-testid="custom-project-success-close"
            className="px-8 py-3 font-display text-sm tracking-wider bg-foreground text-background hover:bg-accent transition-colors duration-300"
          >
            CLOSE
          </button>
        </motion.div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4, ease: smoothEase }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto"
    >
      {/* Backdrop */}
      <motion.div 
        className="fixed inset-0 bg-background/95 backdrop-blur-sm"
        onClick={handleClose}
      />
      
      {/* Modal */}
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 16 }}
        transition={{ duration: 0.5, ease: smoothEase }}
        className="relative w-full max-w-2xl bg-card border border-border z-10 my-8"
      >
        {/* Close button */}
        <button
          onClick={handleClose}
          data-testid="custom-project-close"
          className="absolute top-6 right-6 p-2 text-muted-foreground hover:text-foreground transition-colors duration-300 z-10"
        >
          <X className="w-5 h-5" />
        </button>
        
        {/* Content */}
        <div className="p-8 md:p-10 max-h-[85vh] overflow-y-auto">
          {/* Logo */}
          <div className="mb-8">
            <img 
              src="/frmla-logo.png" 
              alt="FRMLA Creative" 
              className="w-[140px] h-auto"
            />
          </div>
          
          {/* Header */}
          <div className="mb-10">
            <p className="font-body text-xs tracking-[0.2em] text-accent uppercase mb-3">
              CUSTOM PROJECT
            </p>
            <h3 className="font-display text-3xl text-foreground mb-3">GET A GAME PLAN</h3>
            <p className="font-body text-sm text-muted-foreground">
              Tell us about your project and we'll respond with a clear path forward.
            </p>
          </div>
          
          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Full Name */}
            <div>
              <label className="block font-body text-xs tracking-wider text-muted-foreground uppercase mb-2">
                Full Name <span className="text-accent">*</span>
              </label>
              <input
                type="text"
                name="fullName"
                data-testid="custom-project-fullname"
                value={formData.fullName}
                onChange={handleInputChange}
                className={`w-full bg-muted/20 border ${errors.fullName ? 'border-red-500' : 'border-border'} px-4 py-3 font-body text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-accent transition-colors duration-300`}
                placeholder="Your full name"
              />
              {errors.fullName && (
                <p className="mt-1 font-body text-xs text-red-500">{errors.fullName}</p>
              )}
            </div>
            
            {/* Email */}
            <div>
              <label className="block font-body text-xs tracking-wider text-muted-foreground uppercase mb-2">
                Email Address <span className="text-accent">*</span>
              </label>
              <input
                type="email"
                name="email"
                data-testid="custom-project-email"
                value={formData.email}
                onChange={handleInputChange}
                className={`w-full bg-muted/20 border ${errors.email ? 'border-red-500' : 'border-border'} px-4 py-3 font-body text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-accent transition-colors duration-300`}
                placeholder="your@email.com"
              />
              {errors.email && (
                <p className="mt-1 font-body text-xs text-red-500">{errors.email}</p>
              )}
            </div>
            
            {/* Phone */}
            <div>
              <label className="block font-body text-xs tracking-wider text-muted-foreground uppercase mb-2">
                Phone Number <span className="text-accent">*</span>
              </label>
              <input
                type="tel"
                name="phone"
                data-testid="custom-project-phone"
                value={formData.phone}
                onChange={handleInputChange}
                className={`w-full bg-muted/20 border ${errors.phone ? 'border-red-500' : 'border-border'} px-4 py-3 font-body text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-accent transition-colors duration-300`}
                placeholder="(555) 123-4567"
              />
              {errors.phone && (
                <p className="mt-1 font-body text-xs text-red-500">{errors.phone}</p>
              )}
            </div>
            
            {/* Gold divider */}
            <div className="gold-line w-full my-8" />
            
            {/* Project Description */}
            <div>
              <label className="block font-body text-xs tracking-wider text-muted-foreground uppercase mb-2">
                Project Description <span className="text-accent">*</span>
              </label>
              <textarea
                name="projectDescription"
                data-testid="custom-project-description"
                value={formData.projectDescription}
                onChange={handleInputChange}
                rows={4}
                className={`w-full bg-muted/20 border ${errors.projectDescription ? 'border-red-500' : 'border-border'} px-4 py-3 font-body text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-accent transition-colors duration-300 resize-none`}
                placeholder="What are you trying to build? Describe your project, goals, and vision..."
              />
              {errors.projectDescription && (
                <p className="mt-1 font-body text-xs text-red-500">{errors.projectDescription}</p>
              )}
            </div>
            
            {/* Type of Work */}
            <div>
              <label className="block font-body text-xs tracking-wider text-muted-foreground uppercase mb-2">
                Type of Work Needed <span className="text-accent">*</span>
              </label>
              <select
                name="workType"
                data-testid="custom-project-worktype"
                value={formData.workType}
                onChange={handleInputChange}
                className={`w-full bg-muted/20 border ${errors.workType ? 'border-red-500' : 'border-border'} px-4 py-3 font-body text-foreground focus:outline-none focus:border-accent transition-colors duration-300 appearance-none cursor-pointer`}
                style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23888' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 16px center' }}
              >
                {workTypeOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              {errors.workType && (
                <p className="mt-1 font-body text-xs text-red-500">{errors.workType}</p>
              )}
            </div>
            
            {/* Number of Assets */}
            <div>
              <label className="block font-body text-xs tracking-wider text-muted-foreground uppercase mb-2">
                Number of Assets <span className="text-accent">*</span>
              </label>
              <select
                name="assetCount"
                data-testid="custom-project-assetcount"
                value={formData.assetCount}
                onChange={handleInputChange}
                className={`w-full bg-muted/20 border ${errors.assetCount ? 'border-red-500' : 'border-border'} px-4 py-3 font-body text-foreground focus:outline-none focus:border-accent transition-colors duration-300 appearance-none cursor-pointer`}
                style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23888' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 16px center' }}
              >
                {assetCountOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              {errors.assetCount && (
                <p className="mt-1 font-body text-xs text-red-500">{errors.assetCount}</p>
              )}
            </div>
            
            {/* Brand Asset Status */}
            <div>
              <label className="block font-body text-xs tracking-wider text-muted-foreground uppercase mb-3">
                Brand Asset Status <span className="text-muted-foreground/50">(select all that apply)</span>
              </label>
              <div className="space-y-2">
                {brandAssetOptions.map(asset => (
                  <button
                    key={asset.id}
                    type="button"
                    onClick={() => handleBrandAssetToggle(asset.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 border transition-colors duration-300 ${
                      formData.brandAssets.includes(asset.id)
                        ? 'border-accent/60 bg-accent/5'
                        : 'border-border hover:border-muted-foreground/40'
                    }`}
                  >
                    <div className={`w-5 h-5 border-2 rounded flex items-center justify-center transition-all duration-300 ${
                      formData.brandAssets.includes(asset.id)
                        ? 'border-accent bg-accent'
                        : 'border-muted-foreground/30'
                    }`}>
                      {formData.brandAssets.includes(asset.id) && (
                        <Check className="w-3 h-3 text-background" />
                      )}
                    </div>
                    <span className="font-body text-sm text-foreground">{asset.label}</span>
                  </button>
                ))}
              </div>
            </div>
            
            {/* Timeline */}
            <div>
              <label className="block font-body text-xs tracking-wider text-muted-foreground uppercase mb-2">
                Timeline <span className="text-muted-foreground/50">(optional)</span>
              </label>
              <input
                type="text"
                name="timeline"
                data-testid="custom-project-timeline"
                value={formData.timeline}
                onChange={handleInputChange}
                className="w-full bg-muted/20 border border-border px-4 py-3 font-body text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-accent transition-colors duration-300"
                placeholder="e.g., 2 weeks, end of month, flexible..."
              />
            </div>
            
            {/* Additional Notes */}
            <div>
              <label className="block font-body text-xs tracking-wider text-muted-foreground uppercase mb-2">
                Additional Notes <span className="text-muted-foreground/50">(optional)</span>
              </label>
              <textarea
                name="notes"
                data-testid="custom-project-notes"
                value={formData.notes}
                onChange={handleInputChange}
                rows={3}
                className="w-full bg-muted/20 border border-border px-4 py-3 font-body text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-accent transition-colors duration-300 resize-none"
                placeholder="Any other details, references, or context..."
              />
            </div>
            
            {/* File Upload */}
            <div>
              <label className="block font-body text-xs tracking-wider text-muted-foreground uppercase mb-2">
                Upload Files <span className="text-muted-foreground/50">(optional)</span>
              </label>
              <p className="font-body text-xs text-muted-foreground/60 mb-3">
                References, inspiration, existing assets, or any files that help explain your project.
              </p>
              <label className="flex items-center justify-center gap-3 w-full border-2 border-dashed border-border hover:border-muted-foreground/40 px-4 py-8 cursor-pointer transition-colors duration-300">
                <Upload className="w-5 h-5 text-muted-foreground" />
                <span className="font-body text-sm text-muted-foreground">
                  {formData.files.length > 0 
                    ? `${formData.files.length} file(s) selected`
                    : 'Click to upload files'
                  }
                </span>
                <input
                  type="file"
                  multiple
                  onChange={handleFileChange}
                  data-testid="custom-project-files"
                  className="hidden"
                  accept="image/*,.pdf,.ai,.psd,.zip,.doc,.docx"
                />
              </label>
              {formData.files.length > 0 && (
                <div className="mt-2 space-y-1">
                  {formData.files.map((file, index) => (
                    <p key={index} className="font-body text-xs text-muted-foreground/60">
                      {file.name}
                    </p>
                  ))}
                </div>
              )}
            </div>
            
            {/* Submit Error */}
            {submitError && (
              <p className="font-body text-sm text-red-500 text-center">{submitError}</p>
            )}
            
            {/* Submit Button */}
            <div className="pt-4">
              <button
                type="submit"
                data-testid="custom-project-submit"
                disabled={isSubmitting}
                className="w-full py-4 font-display text-lg tracking-wider bg-foreground text-background hover:bg-accent transition-colors duration-400 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
                style={{ transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)' }}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    SUBMITTING...
                  </>
                ) : (
                  'SUBMIT REQUEST'
                )}
              </button>
            </div>
          </form>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default CustomProjectModal;
